import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { enUS, it } from "date-fns/locale";
import { Layout } from "../../components/Layout";
import {
	ResultEntryDialog,
	useResultEntry,
} from "../../components/AthleteDashboard";
import { useLanguage } from "../../contexts/LanguageContext";
import { fetchAPI, getDateLocale, getToday } from "../../lib/utils";
import {
	formatValueByUnit,
	getCategoryColor,
	getInputPropsForUnit,
	normalizeCategoryValue,
	normalizeTimeOrRepsInput,
	parseValueByUnit,
	rankAndFilterExercises,
	getYouTubeEmbedUrl,
} from "../../lib/coachUtils";
import { formatAmrapScoreWithRoundsLabel } from "../../lib/conditioningResultUtils";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "../../components/ui/dialog";
import { Calendar } from "../../components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "../../components/ui/popover";
import {
	BookOpen,
	Calendar as CalendarIcon,
	Loader2,
	Play,
	Plus,
	X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
	buildHistoryDay,
	removeHistoryResult,
	replaceHistoryResult,
} from "./lib/dayAggregator";
import { summarizeResult } from "./lib/resultSummary";
import { HistoryDay } from "./components/HistoryDay";

// ── Helpers ──────────────────────────────────────────────

// Giorni con attività per pagina dello storico: a un atleta che si allena
// quasi ogni giorno sono due settimane, poi si carica scorrendo.
const HISTORY_PAGE_DAYS = 14;

const toDateOnlyString = (value) => {
	if (!value) return getToday();
	if (typeof value === "string") return value.split("T")[0];
	if (!(value instanceof Date)) return getToday();
	const year = value.getFullYear();
	const month = String(value.getMonth() + 1).padStart(2, "0");
	const day = String(value.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const fromDateOnlyString = (value) => new Date(`${value}T12:00:00`);

const shiftDateOnlyString = (value, days) => {
	const d = fromDateOnlyString(value);
	d.setDate(d.getDate() + days);
	return toDateOnlyString(d);
};

// Il deep link `diaryFocus` (Messaggi → «apri nel diario») porta una data e
// l'esercizio da aprire.
const readDiaryFocus = (state) => {
	const focus = state?.diaryFocus;
	if (!focus || typeof focus !== "object") return null;
	return {
		date: toDateOnlyString(focus.date || focus.workout_date || getToday()),
		programId: String(focus.program_id || "").trim() || null,
		dayId: String(focus.day_id || "").trim() || null,
		exerciseId: String(focus.exercise_id || "").trim() || null,
		exerciseName: String(focus.exercise_name || "").trim() || null,
	};
};

const historyUrl = (before) =>
	`/diary/history?days=${HISTORY_PAGE_DAYS}${
		before ? `&before=${encodeURIComponent(before)}` : ""
	}`;


const emptyManualResult = (dateValue = new Date()) => ({
	date: dateValue instanceof Date ? new Date(dateValue) : new Date(),
	library_item_id: "",
	result_kind: "",
	weight_kg: "",
	reps: "",
	time_min: "",
	time_sec: "",
	score: "",
	free_score: "",
	emom_score_text: "",
	time_or_reps_input: "",
	schema_values_raw: {},
	notes: "",
	scaling: "",
});

const mapLibraryCategoryToExerciseType = (category) => {
	const normalized = normalizeCategoryValue(category);
	if (normalized === "conditioning") return "conditioning";
	if (normalized === "cardio") return "cardio";
	return "strength";
};

const flattenPerformanceProfileMap = (domains) => {
	const map = {};
	(domains || []).forEach((domain) => {
		(domain?.exercises || []).forEach((exercise) => {
			if (exercise?.exercise_id && !map[exercise.exercise_id]) {
				map[exercise.exercise_id] = exercise;
			}
		});
	});
	return map;
};

const buildManualLibraryItems = (exercises, templates) => {
	const exerciseItems = (exercises || [])
		.map((exercise, index) => {
			const normalizedId =
				exercise.exercise_id ||
				exercise.id ||
				exercise._id ||
				`legacy_exercise_${index}`;
			const normalizedName =
				exercise.name || exercise.exercise_name || exercise.title || "";

			return {
				item_id: `exercise:${normalizedId}`,
				exercise_id: String(normalizedId),
				exercise_name: normalizedName,
				exercise_type: mapLibraryCategoryToExerciseType(exercise.category),
				category: normalizeCategoryValue(exercise.category) || "strength",
				description: exercise.description || exercise.notes || "",
				video_url: exercise.video_url || null,
				image_url: exercise.image_url || null,
				conditioning_type: null,
				conditioning_config: null,
				library_conditioning_id: null,
				result_schema: [],
			};
		})
		.filter((exercise) => Boolean(String(exercise.exercise_name || "").trim()));

	const conditioningItems = (templates || []).map((template) => ({
		item_id: `conditioning:${template.conditioning_id}`,
		exercise_id: template.conditioning_id,
		exercise_name: template.name,
		exercise_type: "conditioning",
		category: "conditioning",
		description: template.notes || template.conditioning_free_text || "",
		video_url: template.video_url || null,
		image_url: template.image_url || null,
		conditioning_type: template.conditioning_type || null,
		conditioning_config: template,
		library_conditioning_id: template.conditioning_id,
		result_schema: template.result_schema || [],
	}));

	return [...exerciseItems, ...conditioningItems].sort((a, b) =>
		(a.exercise_name || "").localeCompare(b.exercise_name || ""),
	);
};

const getDefaultManualResultKind = (exercise, profileConfig) => {
	if (!exercise) return "";
	const profileUnit = String(profileConfig?.unit || "")
		.trim()
		.toLowerCase();

	if (exercise.exercise_type === "conditioning") {
		if ((exercise.result_schema || []).length > 0) return "schema";
		if (exercise.conditioning_type === "for_time") {
			return profileUnit === "reps" || profileUnit === "time_or_reps"
				? "time_or_reps"
				: "for_time";
		}
		if (exercise.conditioning_type === "amrap") return "score";
		if (exercise.conditioning_type === "emom") return "emom";
		return "free_score";
	}

	switch (profileUnit) {
		case "kg":
			return "weight_reps";
		case "reps":
			return "reps";
		case "seconds":
		case "minutes":
		case "minutes_seconds":
			return "time";
		case "time_or_reps":
		case "meters":
		case "points":
			return "score";
		default:
			return exercise.exercise_type === "cardio" ? "time" : "weight_reps";
	}
};

const getManualResultKindOptions = (exercise, profileConfig, t) => {
	if (!exercise) return [];
	if (exercise.exercise_type === "conditioning") {
		if ((exercise.result_schema || []).length > 0) {
			return [{ value: "schema", label: t("manualResultKindSchema") }];
		}
		if (exercise.conditioning_type === "for_time") {
			const defaultKind = getDefaultManualResultKind(exercise, profileConfig);
			return [
				{
					value: defaultKind,
					label:
						defaultKind === "time_or_reps"
							? t("manualResultKindTimeOrReps")
							: t("manualResultKindForTime"),
				},
				...(defaultKind === "for_time"
					? [
							{
								value: "time_or_reps",
								label: t("manualResultKindTimeOrReps"),
							},
						]
					: [
							{
								value: "for_time",
								label: t("manualResultKindForTime"),
							},
						]),
			];
		}
		if (exercise.conditioning_type === "amrap") {
			return [{ value: "score", label: t("manualResultKindScore") }];
		}
		if (exercise.conditioning_type === "emom") {
			return [{ value: "emom", label: t("manualResultKindFreeScore") }];
		}
		return [{ value: "free_score", label: t("manualResultKindFreeResult") }];
	}

	return [
		{ value: "weight_reps", label: t("manualResultKindWeightReps") },
		{ value: "weight", label: t("manualResultKindWeight") },
		{ value: "reps", label: t("manualResultKindReps") },
		{ value: "time", label: t("manualResultKindTime") },
		{ value: "score", label: t("manualResultKindScore") },
	];
};

const manualResultKindNeedsTime = (kind) =>
	kind === "time" || kind === "for_time";

const manualResultKindNeedsScoreText = (kind) =>
	kind === "free_score" || kind === "emom";

const hasStoredValue = (value) =>
	value !== null && value !== undefined && String(value).trim() !== "";
const storedText = (value) => (hasStoredValue(value) ? String(value) : "");

// Un risultato manuale si corregge col modulo che l'ha creato. Quale campo
// aveva scelto l'atleta si legge da ciò che è salvato, con le stesse regole
// di `handleSaveManualResult` lette al contrario.
const getStoredManualResultKind = (result) => {
	if (result?.schema_values && Object.keys(result.schema_values).length > 0)
		return "schema";
	if (result?.score_type === "time_or_reps") return "time_or_reps";
	if (result?.exercise_type === "conditioning") {
		const conditioningType =
			result.conditioning_type ||
			result.conditioning_config?.conditioning_type ||
			"";
		if (conditioningType === "for_time") return "for_time";
		if (conditioningType === "amrap") return "score";
		if (conditioningType === "emom") return "emom";
		return "free_score";
	}
	const hasWeight = hasStoredValue(result?.weight_kg);
	const hasReps = hasStoredValue(result?.reps);
	if (hasWeight && hasReps) return "weight_reps";
	if (hasWeight) return "weight";
	if (hasReps) return "reps";
	if (hasStoredValue(result?.time_seconds)) return "time";
	if (hasStoredValue(result?.score)) return "score";
	return "weight_reps";
};

// La voce di libreria del risultato: un conditioning si salva col
// `conditioning_id` del template come `exercise_id`.
const manualLibraryItemIdForResult = (result) =>
	result?.exercise_type === "conditioning"
		? `conditioning:${result.library_conditioning_id || result.exercise_id}`
		: `exercise:${result?.exercise_id}`;

// Se l'esercizio non è più in libreria il risultato resta correggibile: la
// voce si ricostruisce da ciò che il risultato si è portato dietro.
const manualLibraryItemFromResult = (result) => ({
	item_id: manualLibraryItemIdForResult(result),
	exercise_id: result.exercise_id,
	exercise_name: result.exercise_name,
	exercise_type: result.exercise_type || "strength",
	category: result.exercise_type || "strength",
	description: "",
	video_url: null,
	image_url: null,
	conditioning_type:
		result.conditioning_type ||
		result.conditioning_config?.conditioning_type ||
		null,
	conditioning_config: result.conditioning_config || null,
	library_conditioning_id: result.library_conditioning_id || null,
	result_schema: result.conditioning_config?.result_schema || [],
});

const manualDraftFromResult = (result) => {
	const kind = getStoredManualResultKind(result);
	const seconds = Number(result.time_seconds);
	const hasTime = hasStoredValue(result.time_seconds) && Number.isFinite(seconds);
	const schemaUnits = Object.fromEntries(
		(result.conditioning_config?.result_schema || []).map((field) => [
			field.name,
			field.unit,
		]),
	);
	return {
		...emptyManualResult(fromDateOnlyString(String(result.date).slice(0, 10))),
		library_item_id: manualLibraryItemIdForResult(result),
		result_kind: kind,
		weight_kg: storedText(result.weight_kg),
		reps: storedText(result.reps),
		time_min: hasTime ? String(Math.floor(seconds / 60)) : "",
		time_sec: hasTime ? String(Math.round(seconds % 60)) : "",
		score: kind === "score" ? storedText(result.score) : "",
		free_score: kind === "free_score" ? storedText(result.score) : "",
		emom_score_text: kind === "emom" ? storedText(result.score) : "",
		time_or_reps_input:
			kind === "time_or_reps" ? storedText(result.score) : "",
		schema_values_raw: Object.fromEntries(
			Object.entries(result.schema_values || {}).map(([name, value]) => [
				name,
				schemaUnits[name]
					? String(formatValueByUnit(value, schemaUnits[name]) ?? "")
					: storedText(value),
			]),
		),
		notes: result.notes || "",
		scaling: result.scaling || "",
	};
};

// Il Diario non inserisce risultati di programma, li corregge soltanto: la
// data viene dal risultato e la libreria serve solo all'id, che in modifica
// il server non riscrive.
const NO_WEEK_DAYS = [];
const NO_EXERCISE_LIBRARY = {};
const diaryResultsPath = (suffix = "") => `/results${suffix}`;
const diaryResultToast = (_sandboxKey, fallback) => fallback;


// ── Component ────────────────────────────────────────────

/**
 * Il Diario dell'atleta: lo storico di ciò che ha **fatto**, dal giorno più
 * recente all'indietro — risultati registrati negli allenamenti, risultati
 * inseriti a mano, note libere. I programmi assegnati e non eseguiti non
 * compaiono: per quello c'è la Dashboard.
 */
export default function Workouts() {
	const { t, language } = useLanguage();
	const calendarLocale = useMemo(
		() => (language === "it" ? it : enUS),
		[language],
	);
	const location = useLocation();
	const navigate = useNavigate();
	const todayStr = getToday();
	const [diaryFocusTarget, setDiaryFocusTarget] = useState(() =>
		readDiaryFocus(location.state),
	);
	// Data da cui parte lo storico (inclusa); null = dall'attività più recente.
	const [anchorDate, setAnchorDate] = useState(
		() => readDiaryFocus(location.state)?.date || null,
	);
	const [historyDays, setHistoryDays] = useState([]);
	const [nextBefore, setNextBefore] = useState(null);
	const [hasMore, setHasMore] = useState(false);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [calendarOpen, setCalendarOpen] = useState(false);
	const [activeProgramIds, setActiveProgramIds] = useState([]);
	const [activeDayIds, setActiveDayIds] = useState(new Set());
	const [isAddOpen, setIsAddOpen] = useState(false);
	const [manualSearch, setManualSearch] = useState("");
	const [manualResultDraft, setManualResultDraft] = useState(
		emptyManualResult(new Date()),
	);
	const [manualLibraryItems, setManualLibraryItems] = useState([]);
	const [manualLibraryLoading, setManualLibraryLoading] = useState(false);
	const [performanceProfileMap, setPerformanceProfileMap] = useState({});
	const [manualSubmitting, setManualSubmitting] = useState(false);
	const [manualVideoPreviewState, setManualVideoPreviewState] = useState(null);
	const [deleteConfirmState, setDeleteConfirmState] = useState(null);
	// Il risultato manuale che il modale manuale sta correggendo; null = nuovo.
	const [manualEditingResult, setManualEditingResult] = useState(null);

	// Correzione di un risultato di programma: è lo stesso flusso della
	// Dashboard (`useResultEntry`), sulla riga del programma che il server
	// aggancia al risultato. La sua mappa dei risultati qui contiene quelli
	// aperti in modifica; ciò che il flusso ci scrive — corretto o eliminato —
	// si riporta nello storico.
	const [entryResults, setEntryResultsState] = useState({});
	const entryResultsRef = useRef({});
	const setEntryResults = useCallback((update) => {
		const prev = entryResultsRef.current;
		const next = typeof update === "function" ? update(prev) : update;
		entryResultsRef.current = next;
		setEntryResultsState(next);
		Object.keys(prev).forEach((resultId) => {
			if (!next[resultId]) {
				setHistoryDays((days) => removeHistoryResult(days, resultId));
			} else if (next[resultId] !== prev[resultId]) {
				setHistoryDays((days) => replaceHistoryResult(days, next[resultId]));
			}
		});
	}, []);
	const resultEntry = useResultEntry({
		todayResults: entryResults,
		setTodayResults: setEntryResults,
		resultsPath: diaryResultsPath,
		sandboxToast: diaryResultToast,
		currentWeekDays: NO_WEEK_DAYS,
		currentDay: null,
		exerciseLibrary: NO_EXERCISE_LIBRARY,
	});
	const { startEdit } = resultEntry;
	// Ogni ricarica dello storico invalida le pagine ancora in volo della
	// precedente: un «carica altri» partito prima di un salto di data non
	// deve accodare giorni dell'altro periodo.
	const historyRequestRef = useRef(0);
	const loadingMoreRef = useRef(false);
	const sentinelRef = useRef(null);
	const loadMoreRef = useRef(null);

	const loadHistory = async (anchor) => {
		const requestId = ++historyRequestRef.current;
		setLoading(true);
		try {
			const page = await fetchAPI(
				historyUrl(anchor ? shiftDateOnlyString(anchor, 1) : null),
			);
			if (requestId !== historyRequestRef.current) return;
			setHistoryDays(page?.days || []);
			setNextBefore(page?.next_before || null);
			setHasMore(Boolean(page?.has_more));
		} catch (error) {
			if (requestId !== historyRequestRef.current) return;
			setHistoryDays([]);
			setHasMore(false);
			toast.error(t("diaryHistoryLoadError"));
		} finally {
			if (requestId === historyRequestRef.current) setLoading(false);
		}
	};

	const loadMore = async () => {
		if (!nextBefore || loadingMoreRef.current) return;
		const requestId = historyRequestRef.current;
		loadingMoreRef.current = true;
		setLoadingMore(true);
		try {
			const page = await fetchAPI(historyUrl(nextBefore));
			if (requestId !== historyRequestRef.current) return;
			setHistoryDays((current) => [...current, ...(page?.days || [])]);
			setNextBefore(page?.next_before || null);
			setHasMore(Boolean(page?.has_more));
		} catch (error) {
			toast.error(t("diaryHistoryLoadError"));
		} finally {
			loadingMoreRef.current = false;
			setLoadingMore(false);
		}
	};
	loadMoreRef.current = loadMore;

	useEffect(() => {
		loadHistory(anchorDate);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [anchorDate]);

	// Scorrendo verso il fondo si caricano da sé i giorni precedenti; il
	// bottone resta per chi non scorre (o senza IntersectionObserver).
	useEffect(() => {
		const node = sentinelRef.current;
		if (!node || !hasMore || typeof IntersectionObserver === "undefined")
			return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting))
					loadMoreRef.current?.();
			},
			{ rootMargin: "400px 0px" },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [hasMore, historyDays.length]);

	// Solo per il link «Apri l'allenamento»: la Dashboard apre i programmi attivi.
	useEffect(() => {
		fetchAPI("/workouts/active-programs")
			.then((programs) => {
				setActiveProgramIds(
					(programs || []).map((program) => program?.program_id).filter(Boolean),
				);
				const dayIds = new Set();
				(programs || []).forEach((program) => {
					(program?.weeks || []).forEach((week) => {
						(week?.days || []).forEach((day) => {
							if (day?.day_id) dayIds.add(day.day_id);
						});
					});
				});
				setActiveDayIds(dayIds);
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		const focus = readDiaryFocus(location.state);
		if (!focus) return;
		setDiaryFocusTarget(focus);
		setAnchorDate(focus.date);
		navigate(location.pathname, { replace: true, state: null });
	}, [location.pathname, location.state, navigate]);

	useEffect(() => {
		if (!isAddOpen || manualLibraryItems.length > 0 || manualLibraryLoading)
			return;
		const loadManualResultContext = async () => {
			setManualLibraryLoading(true);
			try {
				const [exerciseLibrary, conditioningLibrary, performanceProfile] =
					await Promise.all([
						fetchAPI("/exercises?scope=visible"),
						fetchAPI("/conditioning-library"),
						fetchAPI("/profile/performance-profile"),
					]);
				const items = buildManualLibraryItems(
					exerciseLibrary || [],
					conditioningLibrary || [],
				);
				const profileMap = flattenPerformanceProfileMap(
					performanceProfile?.domains || [],
				);
				setManualLibraryItems(items);
				setPerformanceProfileMap(profileMap);
			} catch (error) {
				toast.error(t("manualResultLoadError"));
			} finally {
				setManualLibraryLoading(false);
			}
		};

		loadManualResultContext();
	}, [isAddOpen, manualLibraryItems.length, manualLibraryLoading, t]);

	const selectedManualExercise = useMemo(
		() =>
			manualLibraryItems.find(
				(item) => item.item_id === manualResultDraft.library_item_id,
			) ||
			(manualEditingResult
				? manualLibraryItemFromResult(manualEditingResult)
				: null),
		[manualLibraryItems, manualResultDraft.library_item_id, manualEditingResult],
	);

	const selectedManualProfileConfig = useMemo(() => {
		if (!selectedManualExercise?.exercise_id) return null;
		return performanceProfileMap[selectedManualExercise.exercise_id] || null;
	}, [performanceProfileMap, selectedManualExercise]);

	const manualResultKindOptions = useMemo(
		() =>
			getManualResultKindOptions(
				selectedManualExercise,
				selectedManualProfileConfig,
				t,
			),
		[selectedManualExercise, selectedManualProfileConfig, t],
	);

	const manualAmrapRoundsPreview = useMemo(() => {
		if (manualResultDraft.result_kind !== "score") return "";
		if (selectedManualExercise?.exercise_type !== "conditioning") return "";
		if (selectedManualExercise?.conditioning_type !== "amrap") return "";
		return formatAmrapScoreWithRoundsLabel(
			manualResultDraft.score,
			selectedManualExercise?.conditioning_config?.conditioning_exercises ||
				selectedManualExercise?.conditioning_exercises ||
				[],
			language,
		);
	}, [
		manualResultDraft.result_kind,
		manualResultDraft.score,
		selectedManualExercise,
		language,
	]);

	const filteredManualLibraryItems = useMemo(() => {
		const query = manualSearch.trim();
		if (!query) return [];
		return rankAndFilterExercises(manualLibraryItems, query, "exercise_name");
	}, [manualLibraryItems, manualSearch]);

	// ── History actions ──

	const jumpToDate = (date) => {
		setCalendarOpen(false);
		setDiaryFocusTarget(null);
		const dateStr = toDateOnlyString(date);
		setAnchorDate(dateStr >= todayStr ? null : dateStr);
	};

	const backToToday = () => {
		setDiaryFocusTarget(null);
		setAnchorDate(null);
	};

	const workoutHrefFor = (dateStr) => (group) => {
		if (group.isManual) return null;
		if (
			!activeProgramIds.includes(group.programId) ||
			!activeDayIds.has(group.dayId)
		)
			return null;
		return `/dashboard?program_id=${encodeURIComponent(
			group.programId,
		)}&day_id=${encodeURIComponent(group.dayId || "")}&date=${encodeURIComponent(
			dateStr,
		)}`;
	};

	const dayModels = useMemo(
		() => historyDays.map((day) => buildHistoryDay(day)),
		[historyDays],
	);

	// ── Manual result actions ──

	const openManualResultDialog = () => {
		setManualSearch("");
		setManualVideoPreviewState(null);
		setManualEditingResult(null);
		setManualResultDraft(emptyManualResult(new Date()));
		setIsAddOpen(true);
	};

	// Ogni risultato si corregge col modulo con cui è nato: quello della
	// Dashboard se viene da un programma, quello manuale se inserito a mano.
	const editResult = (exercise) => {
		const result = exercise?.result;
		if (!result?.result_id) return;
		const programExercise = result.prescription?.exercise;
		if (programExercise) {
			setEntryResults((prev) => ({ ...prev, [result.result_id]: result }));
			startEdit(
				result.result_id,
				result,
				programExercise,
				result.program_id,
				result.day_id,
			);
			return;
		}
		setManualSearch("");
		setManualVideoPreviewState(null);
		setManualEditingResult(result);
		setManualResultDraft(manualDraftFromResult(result));
		setIsAddOpen(true);
	};

	const askDeleteResult = (exercise) => {
		setDeleteConfirmState({
			result_id: exercise.result?.result_id,
			exercise_name: exercise.exercise_name,
			result_value:
				summarizeResult(exercise.result, exercise, language) ||
				t("resultValueNotAvailable"),
		});
	};

	const handleDeleteResult = async () => {
		if (!deleteConfirmState) return;
		const { result_id } = deleteConfirmState;
		try {
			await fetchAPI(`/results/${result_id}`, { method: "DELETE" });
			toast.success(t("resultDeleted"));
			setDeleteConfirmState(null);
			setHistoryDays((days) => removeHistoryResult(days, result_id));
		} catch (error) {
			toast.error(`Errore: ${error.message}`);
		}
	};

	const handleManualFieldChange = (field, value) => {
		setManualResultDraft((current) => ({
			...current,
			[field]: value,
		}));
	};

	const handleManualSchemaValueChange = (field, value) => {
		setManualResultDraft((current) => ({
			...current,
			schema_values_raw: {
				...current.schema_values_raw,
				[field]: value,
			},
		}));
	};

	const handleManualExerciseSelect = (itemId) => {
		const exercise = manualLibraryItems.find((item) => item.item_id === itemId);
		const profileConfig = exercise
			? performanceProfileMap[exercise.exercise_id] || null
			: null;
		setManualResultDraft((current) => ({
			...emptyManualResult(current.date),
			date: current.date,
			library_item_id: itemId,
			result_kind: getDefaultManualResultKind(exercise, profileConfig),
		}));
		// Collapse search box once an exercise is chosen
		setManualSearch("");
	};

	const openManualExercisePreview = (exercise) => {
		const embedUrl = getYouTubeEmbedUrl(exercise?.video_url);
		if (!embedUrl) return;
		setManualVideoPreviewState({
			exerciseName: exercise.exercise_name,
			embedUrl,
		});
	};

	const closeManualExercisePreview = () => {
		setManualVideoPreviewState(null);
	};

	const handleSaveManualResult = async () => {
		if (!selectedManualExercise) {
			toast.error(t("manualResultSelectExercise"));
			return;
		}

		const resultKind =
			manualResultDraft.result_kind ||
			getDefaultManualResultKind(
				selectedManualExercise,
				selectedManualProfileConfig,
			);

		const payload = {
			date: toDateOnlyString(manualResultDraft.date),
			exercise_id: selectedManualExercise.exercise_id,
			exercise_name: selectedManualExercise.exercise_name,
			exercise_type: selectedManualExercise.exercise_type,
			program_id: null,
			day_id: null,
			notes: manualResultDraft.notes?.trim() || null,
			scaling: manualResultDraft.scaling?.trim() || null,
			score_type: null,
		};

		if (selectedManualExercise.exercise_type === "conditioning") {
			payload.conditioning_type =
				selectedManualExercise.conditioning_type || null;
			payload.conditioning_config =
				selectedManualExercise.conditioning_config || null;
			payload.library_conditioning_id =
				selectedManualExercise.library_conditioning_id || null;
		}

		if (resultKind === "weight_reps") {
			payload.weight_kg = manualResultDraft.weight_kg
				? Number(String(manualResultDraft.weight_kg).replace(/,/g, "."))
				: null;
			payload.reps = manualResultDraft.reps
				? parseInt(manualResultDraft.reps, 10)
				: null;
		} else if (resultKind === "weight") {
			payload.weight_kg = manualResultDraft.weight_kg
				? Number(String(manualResultDraft.weight_kg).replace(/,/g, "."))
				: null;
		} else if (resultKind === "reps") {
			payload.reps = manualResultDraft.reps
				? parseInt(manualResultDraft.reps, 10)
				: null;
		} else if (manualResultKindNeedsTime(resultKind)) {
			const minutes = parseInt(manualResultDraft.time_min || "0", 10) || 0;
			const seconds = parseInt(manualResultDraft.time_sec || "0", 10) || 0;
			payload.time_seconds = minutes * 60 + seconds;
			payload.score_type = resultKind === "for_time" ? "for_time" : null;
		} else if (resultKind === "score") {
			payload.score = manualResultDraft.score?.trim() || null;
		} else if (resultKind === "time_or_reps") {
			const normalizedValue = normalizeTimeOrRepsInput(
				manualResultDraft.time_or_reps_input,
			);
			if (!normalizedValue) {
				toast.error(t("manualResultInvalidValue"));
				return;
			}
			payload.score = normalizedValue;
			payload.score_type = "time_or_reps";
		} else if (resultKind === "schema") {
			const schemaValues = {};
			(selectedManualExercise.result_schema || []).forEach((field) => {
				const rawValue = manualResultDraft.schema_values_raw[field.name];
				const parsedValue = parseValueByUnit(rawValue, field.unit);
				if (
					parsedValue !== null &&
					parsedValue !== undefined &&
					parsedValue !== ""
				) {
					schemaValues[field.name] = parsedValue;
				}
			});
			if (Object.keys(schemaValues).length === 0) {
				toast.error(t("manualResultFillAtLeastOne"));
				return;
			}
			payload.schema_values = schemaValues;
		} else if (manualResultKindNeedsScoreText(resultKind)) {
			payload.score =
				(resultKind === "emom"
					? manualResultDraft.emom_score_text
					: manualResultDraft.free_score
				)?.trim() || null;
		}

		if (
			!payload.weight_kg &&
			!payload.reps &&
			!payload.time_seconds &&
			!payload.score &&
			!payload.schema_values
		) {
			toast.error(t("manualResultFillBeforeSave"));
			return;
		}

		setManualSubmitting(true);
		if (manualEditingResult) {
			try {
				const updated = await fetchAPI(
					`/results/${manualEditingResult.result_id}`,
					{ method: "PUT", body: JSON.stringify(payload) },
				);
				toast.success(t("resultUpdated"));
				setIsAddOpen(false);
				setManualEditingResult(null);
				setHistoryDays((days) => replaceHistoryResult(days, updated));
			} catch (error) {
				toast.error(`Errore: ${error.message}`);
			} finally {
				setManualSubmitting(false);
			}
			return;
		}
		try {
			await fetchAPI("/results", {
				method: "POST",
				body: JSON.stringify(payload),
			});
			toast.success(t("manualResultAdded"));
			const savedDateStr = toDateOnlyString(manualResultDraft.date);
			setIsAddOpen(false);
			setManualResultDraft(emptyManualResult(new Date()));
			// Il risultato deve comparire: se lo storico parte da una data
			// precedente a quella salvata, si torna all'inizio.
			if (anchorDate && savedDateStr > anchorDate) setAnchorDate(null);
			else await loadHistory(anchorDate);
		} catch (error) {
			toast.error(`Errore: ${error.message}`);
		} finally {
			setManualSubmitting(false);
		}
	};


	const anchorLabel = anchorDate
		? fromDateOnlyString(anchorDate).toLocaleDateString(
				getDateLocale(language),
				{ day: "numeric", month: "long", year: "numeric" },
			)
		: "";

	return (
		<Layout>
			<div
				className="px-4 pb-24 pt-4 max-w-2xl mx-auto"
				data-testid="workouts-page"
			>
				{/* ── Header ── */}
				<div className="flex items-center justify-between mb-4 gap-2">
					<h1 className="font-heading text-2xl">{t("diary")}</h1>
					<div className="flex items-center gap-2">
						<Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
							<PopoverTrigger asChild>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="h-11 w-11 rounded-sm text-muted-foreground hover:text-foreground"
									aria-label={t("diaryHistoryJumpToDate")}
									title={t("diaryHistoryJumpToDate")}
									data-testid="diary-jump-to-date-btn"
								>
									<CalendarIcon className="w-4 h-4" />
								</Button>
							</PopoverTrigger>
							<PopoverContent
								className="w-auto p-0 bg-card border-border"
								align="end"
							>
								<Calendar
									mode="single"
									locale={calendarLocale}
									selected={
										anchorDate ? fromDateOnlyString(anchorDate) : new Date()
									}
									defaultMonth={
										anchorDate ? fromDateOnlyString(anchorDate) : new Date()
									}
									disabled={(date) => toDateOnlyString(date) > todayStr}
									onSelect={(date) => {
										if (date) jumpToDate(date);
									}}
									className="rounded-sm"
								/>
							</PopoverContent>
						</Popover>
						<Button
							onClick={openManualResultDialog}
							className="bg-volt hover:bg-volt/90 rounded-sm h-11"
							data-testid="add-manual-result-btn"
						>
							<Plus className="w-4 h-4 mr-2" />
							{t("addManualResult")}
						</Button>
					</div>
				</div>

				{anchorDate && (
					<div className="flex items-center justify-between gap-2 mb-4">
						<span className="text-sm text-muted-foreground">
							{t("diaryHistoryUntil")} {anchorLabel}
						</span>
						<button
							type="button"
							onClick={backToToday}
							className="flex-shrink-0 text-[11px] px-3 py-1 min-h-8 rounded-full border border-volt/30 text-volt bg-volt/5 hover:bg-volt/10 active:bg-volt/20 transition-colors font-medium"
						>
							{t("diaryHistoryBackToToday")}
						</button>
					</div>
				)}

				{/* ── Storico ── */}
				{loading ? (
					<div className="flex items-center justify-center py-16">
						<div className="spinner"></div>
					</div>
				) : dayModels.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-16 text-center">
						<BookOpen className="w-10 h-10 text-muted-foreground mb-3" />
						{anchorDate ? (
							<p className="text-sm text-muted-foreground mb-4">
								{t("diaryHistoryNothingBefore")}
							</p>
						) : (
							<>
								<p className="font-heading text-lg mb-1">
									{t("diaryHistoryEmptyTitle")}
								</p>
								<p className="text-sm text-muted-foreground mb-4 max-w-xs">
									{t("diaryHistoryEmptyText")}
								</p>
								<Button
									onClick={openManualResultDialog}
									className="bg-volt hover:bg-volt/90 rounded-sm h-11"
								>
									<Plus className="w-4 h-4 mr-2" />
									{t("addManualResult")}
								</Button>
							</>
						)}
					</div>
				) : (
					<div className="space-y-6">
						{dayModels.map((day) => (
							<HistoryDay
								key={day.date}
								day={day}
								todayStr={todayStr}
								workoutHref={workoutHrefFor(day.date)}
								focusTarget={
									diaryFocusTarget?.date === day.date ? diaryFocusTarget : null
								}
								onDeleteResult={askDeleteResult}
								onEditResult={editResult}
							/>
						))}

						<div ref={sentinelRef} className="flex justify-center pt-2">
							{hasMore ? (
								<Button
									type="button"
									variant="outline"
									onClick={loadMore}
									disabled={loadingMore}
									className="rounded-sm border-border h-11"
								>
									{loadingMore && (
										<Loader2 className="w-4 h-4 mr-2 animate-spin" />
									)}
									{t("diaryHistoryLoadMore")}
								</Button>
							) : (
								<span className="text-[11px] uppercase tracking-wider text-muted-foreground/60">
									{t("diaryHistoryEnd")}
								</span>
							)}
						</div>
					</div>
				)}
			</div>

			{/* ── Manual result dialog ── */}
			<Dialog
				open={isAddOpen}
				onOpenChange={(open) => {
					setIsAddOpen(open);
					if (!open) setManualEditingResult(null);
				}}
			>
				<DialogContent className="bg-card border-border max-w-xl dialog-mobile-safe">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{manualEditingResult
								? t("editResultTitle")
								: t("addManualResult")}
						</DialogTitle>
					</DialogHeader>

					<div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
						<div className="space-y-2">
							<Label className="label-tactical">{t("date")}</Label>
							<Input
								type="date"
								value={toDateOnlyString(manualResultDraft.date)}
								onChange={(event) =>
									handleManualFieldChange(
										"date",
										fromDateOnlyString(event.target.value),
									)
								}
								// In correzione data ed esercizio restano quelli del
								// risultato: la PUT non li riscrive.
								disabled={Boolean(manualEditingResult)}
								className="bg-secondary border-transparent rounded-sm h-11"
							/>
						</div>

						{!manualEditingResult && (
						<div className="space-y-3">
							<div className="relative">
								<Input
									value={manualSearch}
									onChange={(event) => setManualSearch(event.target.value)}
									className="bg-secondary border-transparent rounded-sm h-11 pr-10"
									placeholder={t("manualResultSearchPlaceholder")}
								/>
								{manualSearch && (
									<button
										type="button"
										onClick={() => setManualSearch("")}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
									>
										<X className="w-4 h-4" />
									</button>
								)}
							</div>
							<div className="border border-border rounded-sm overflow-hidden max-h-72 overflow-y-auto">
								{manualLibraryLoading ? (
									<div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
										<Loader2 className="w-4 h-4 animate-spin" />
										{t("loading")}
									</div>
								) : manualSearch.trim() ? (
									filteredManualLibraryItems.length === 0 ? (
										<div className="py-8 text-center text-sm text-muted-foreground">
											{t("manualResultNoExercisesFound")}
										</div>
									) : (
										filteredManualLibraryItems.map((item) => {
											const canPreview = Boolean(
												getYouTubeEmbedUrl(item.video_url),
											);
											return (
												<button
													key={item.item_id}
													type="button"
													onClick={() =>
														handleManualExerciseSelect(item.item_id)
													}
													className={`w-full text-left px-3 py-3 border-b last:border-b-0 border-border ${manualResultDraft.library_item_id === item.item_id ? "bg-secondary/80" : ""}`}
												>
													<div className="flex items-start justify-between gap-3">
														<div className="min-w-0 flex-1">
															<div className="font-medium text-sm truncate">
																{item.exercise_name}
															</div>
															{item.description && (
																<div className="text-xs text-muted-foreground mt-1 line-clamp-2">
																	{item.description}
																</div>
															)}
														</div>
														<div className="flex items-center gap-1 shrink-0">
															<span
																className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border ${getCategoryColor(item.category)}`}
															>
																{item.category}
															</span>
															{canPreview && (
																<Button
																	type="button"
																	variant="ghost"
																	size="icon"
																	className="h-7 w-7 text-muted-foreground hover:text-foreground"
																	onClick={(event) => {
																		event.stopPropagation();
																		openManualExercisePreview(item);
																	}}
																>
																	<Play className="w-3.5 h-3.5" />
																</Button>
															)}
														</div>
													</div>
												</button>
											);
										})
									)
								) : (
									<div className="py-8 px-4 text-center text-sm text-muted-foreground">
										{t("manualResultSearchPlaceholder")}
									</div>
								)}
							</div>
						</div>
						)}

						{selectedManualExercise && (
							<>
								<div className="rounded-sm border border-border bg-secondary/30 p-4 space-y-2">
									<div className="flex items-center justify-between gap-3">
										<div>
											<p className="font-heading text-lg">
												{selectedManualExercise.exercise_name}
											</p>
											<p className="text-xs text-muted-foreground">
												{selectedManualExercise.exercise_type === "conditioning"
													? selectedManualExercise.conditioning_type ||
														"conditioning"
													: selectedManualExercise.category}
											</p>
										</div>
										{selectedManualProfileConfig?.unit && (
											<span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border border-volt/40 text-volt">
												{t("manualResultProfileUnit")}:{" "}
												{selectedManualProfileConfig.unit}
											</span>
										)}
									</div>
								</div>

								{manualResultKindOptions.length > 0 && (
									<div className="space-y-2">
										<Label className="label-tactical">
											{t("manualResultType")}
										</Label>
										<Select
											value={manualResultDraft.result_kind}
											onValueChange={(value) =>
												handleManualFieldChange("result_kind", value)
											}
										>
											<SelectTrigger className="bg-secondary border-transparent rounded-sm h-11">
												<SelectValue
													placeholder={t("manualResultSelectTypePlaceholder")}
												/>
											</SelectTrigger>
											<SelectContent className="bg-card border-border">
												{manualResultKindOptions.map((option) => (
													<SelectItem key={option.value} value={option.value}>
														{option.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								)}

								{["weight_reps", "weight"].includes(
									manualResultDraft.result_kind,
								) && (
									<div className="space-y-2">
										<Label className="label-tactical">{t("weightKg")}</Label>
										<Input
											type="text"
											inputMode="decimal"
											pattern="[0-9]*[.,]?[0-9]*"
											value={manualResultDraft.weight_kg}
											onChange={(event) =>
												handleManualFieldChange("weight_kg", event.target.value)
											}
											className="bg-secondary border-transparent rounded-sm h-11"
											placeholder="80"
										/>
									</div>
								)}

								{["weight_reps", "reps"].includes(
									manualResultDraft.result_kind,
								) && (
									<div className="space-y-2">
										<Label className="label-tactical">{t("reps")}</Label>
										<Input
											type="number"
											step="1"
											value={manualResultDraft.reps}
											onChange={(event) =>
												handleManualFieldChange("reps", event.target.value)
											}
											className="bg-secondary border-transparent rounded-sm h-11"
											placeholder="8"
										/>
									</div>
								)}

								{manualResultKindNeedsTime(manualResultDraft.result_kind) && (
									<div className="grid grid-cols-2 gap-3">
										<div className="space-y-2">
											<Label className="label-tactical">{t("minutes")}</Label>
											<Input
												type="number"
												step="1"
												value={manualResultDraft.time_min}
												onChange={(event) =>
													handleManualFieldChange(
														"time_min",
														event.target.value,
													)
												}
												className="bg-secondary border-transparent rounded-sm h-11"
												placeholder="12"
											/>
										</div>
										<div className="space-y-2">
											<Label className="label-tactical">{t("seconds")}</Label>
											<Input
												type="number"
												step="1"
												value={manualResultDraft.time_sec}
												onChange={(event) =>
													handleManualFieldChange(
														"time_sec",
														event.target.value,
													)
												}
												className="bg-secondary border-transparent rounded-sm h-11"
												placeholder="30"
											/>
										</div>
									</div>
								)}

								{manualResultDraft.result_kind === "score" && (
									<div className="space-y-2">
										<Label className="label-tactical">{t("score")}</Label>
										<Input
											value={manualResultDraft.score}
											onChange={(event) =>
												handleManualFieldChange("score", event.target.value)
											}
											className="bg-secondary border-transparent rounded-sm h-11"
											placeholder={t("amrapTotalRepsPlaceholder")}
										/>
										<p className="text-[11px] text-muted-foreground">
											{t("amrapTotalRepsHint")}
										</p>
										{manualAmrapRoundsPreview ? (
											<p className="text-[11px] text-muted-foreground">
												{manualAmrapRoundsPreview}
											</p>
										) : null}
									</div>
								)}

								{manualResultDraft.result_kind === "time_or_reps" && (
									<div className="space-y-2">
										<Label className="label-tactical">
											{t("timeOrRepsLabel")}
										</Label>
										<Input
											value={manualResultDraft.time_or_reps_input}
											onChange={(event) =>
												handleManualFieldChange(
													"time_or_reps_input",
													event.target.value,
												)
											}
											className="bg-secondary border-transparent rounded-sm h-11"
											placeholder={t("timeOrRepsPlaceholder")}
										/>
									</div>
								)}

								{manualResultKindNeedsScoreText(
									manualResultDraft.result_kind,
								) && (
									<div className="space-y-2">
										<Label className="label-tactical">{t("result")}</Label>
										<Input
											value={
												manualResultDraft.result_kind === "emom"
													? manualResultDraft.emom_score_text
													: manualResultDraft.free_score
											}
											onChange={(event) =>
												handleManualFieldChange(
													manualResultDraft.result_kind === "emom"
														? "emom_score_text"
														: "free_score",
													event.target.value,
												)
											}
											className="bg-secondary border-transparent rounded-sm h-11"
											placeholder={t("manualResultFreePlaceholder")}
										/>
									</div>
								)}

								{manualResultDraft.result_kind === "schema" && (
									<div className="space-y-3">
										{(selectedManualExercise.result_schema || []).map(
											(field) => {
												const inputProps = getInputPropsForUnit(field.unit);
												return (
													<div key={field.name} className="space-y-2">
														<Label className="label-tactical">
															{field.label || field.name}
															{field.unit ? ` (${field.unit})` : ""}
														</Label>
														<Input
															{...inputProps}
															value={
																manualResultDraft.schema_values_raw[
																	field.name
																] || ""
															}
															onChange={(event) =>
																handleManualSchemaValueChange(
																	field.name,
																	event.target.value,
																)
															}
															className="bg-secondary border-transparent rounded-sm h-11"
														/>
													</div>
												);
											},
										)}
									</div>
								)}

								<div className="space-y-2">
									<Label className="label-tactical">{t("scalingLabel")}</Label>
									<Input
										value={manualResultDraft.scaling}
										onChange={(event) =>
											handleManualFieldChange("scaling", event.target.value)
										}
										className="bg-secondary border-transparent rounded-sm h-11"
										placeholder={t("scalingPlaceholder")}
									/>
								</div>

								<div className="space-y-2">
									<Label className="label-tactical">{t("notes")}</Label>
									<Textarea
										value={manualResultDraft.notes}
										onChange={(event) =>
											handleManualFieldChange("notes", event.target.value)
										}
										className="bg-secondary border-transparent rounded-sm min-h-[90px]"
										placeholder={t("manualResultNotePlaceholder")}
									/>
								</div>
							</>
						)}

						<Button
							onClick={handleSaveManualResult}
							disabled={!selectedManualExercise || manualSubmitting}
							className="w-full bg-volt hover:bg-volt/90 rounded-sm h-12 text-base"
							data-testid="save-manual-result-btn"
						>
							{manualSubmitting ? (
								<>
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
									{t("manualResultSaving")}
								</>
							) : (
								t("manualResultSave")
							)}
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Video preview Dialog – same pattern as AthleteDashboard */}
			<Dialog
				open={!!manualVideoPreviewState}
				onOpenChange={closeManualExercisePreview}
			>
				<DialogContent className="max-w-lg bg-card border-border rounded-sm p-0 gap-0 max-h-[90vh] overflow-y-auto">
					<DialogDescription className="hidden" />
					<DialogHeader className="px-4 py-3 border-b border-border">
						<DialogTitle className="font-heading text-base">
							{manualVideoPreviewState?.exerciseName}
						</DialogTitle>
					</DialogHeader>
					<div className="p-4">
						{manualVideoPreviewState?.embedUrl && (
							<div className="aspect-video rounded-sm overflow-hidden">
								<iframe
									src={manualVideoPreviewState.embedUrl}
									title={manualVideoPreviewState.exerciseName}
									className="w-full h-full"
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
									allowFullScreen
								/>
							</div>
						)}
					</div>
				</DialogContent>
			</Dialog>

			{/* Correzione di un risultato di programma: il modale della Dashboard */}
			<ResultEntryDialog entry={resultEntry} />

			{/* Delete result confirmation Dialog */}
			<Dialog
				open={!!deleteConfirmState}
				onOpenChange={(open) => {
					if (!open) setDeleteConfirmState(null);
				}}
			>
				<DialogContent className="max-w-sm bg-card border-border rounded-sm p-0 gap-0">
					<DialogDescription className="hidden" />
					<DialogHeader className="px-4 py-3 border-b border-border">
						<DialogTitle className="font-heading text-base">
							{t("deleteResultTitle")}
						</DialogTitle>
					</DialogHeader>
					<div className="p-4 space-y-4">
						<p className="text-sm text-muted-foreground">
							{t("deleteResultConfirm")}
						</p>
						{deleteConfirmState?.exercise_name && (
							<div className="space-y-1">
								<p className="text-xs text-muted-foreground uppercase tracking-wider">
									{t("exerciseLabel")}
								</p>
								<p className="text-sm font-medium truncate">
									{deleteConfirmState.exercise_name}
								</p>
							</div>
						)}
						{deleteConfirmState?.result_value && (
							<div className="space-y-1">
								<p className="text-xs text-muted-foreground uppercase tracking-wider">
									{t("resultLabel")}
								</p>
								<p className="text-sm text-foreground truncate">
									{deleteConfirmState.result_value}
								</p>
							</div>
						)}
						<div className="flex gap-3">
							<Button
								variant="outline"
								className="flex-1 rounded-sm border-border"
								onClick={() => setDeleteConfirmState(null)}
							>
								{t("cancel")}
							</Button>
							<Button
								className="flex-1 rounded-sm bg-red-600 hover:bg-red-700 text-white"
								onClick={handleDeleteResult}
							>
								{t("deleteResultConfirmBtn")}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</Layout>
	);
}
